import type { CaseFilters } from "../../services";
import type { QueueSortState } from "./pathologistReviewQueueView";
import { ALL_INCOMING_COLUMNS, ALL_QUEUE_COLUMNS } from "./queueColumnConfig";

export const PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY =
  "pathologist_review_queue_view_v2";
export const LEGACY_PATHOLOGIST_QUEUE_VIEW_PREFERENCE_KEY =
  "pathologist_review_queue_view_v1";
export const PATHOLOGIST_QUEUE_VIEW_VERSION = 2;

export type PathologistQueueViewTab =
  | "review"
  | "sentOut"
  | "incoming"
  | "all"
  | "completed"
  | "corrections";

export interface PathologistQueueTabView {
  filters: CaseFilters;
  sort: QueueSortState | null;
  sorts?: QueueSortState[];
  page?: number;
  blockedPage?: number;
  summaryFilter?: string | null;
}

export interface PathologistQueueViewPreference
  extends Record<string, unknown> {
  version: 2;
  tabs: Partial<Record<PathologistQueueViewTab, PathologistQueueTabView>>;
}

export interface DecodedPathologistQueueView {
  value: PathologistQueueViewPreference;
  invalidTabs: Set<PathologistQueueViewTab>;
}

export const PATHOLOGIST_QUEUE_VIEW_TABS: PathologistQueueViewTab[] = [
  "review",
  "sentOut",
  "incoming",
  "all",
  "completed",
  "corrections",
];

const FILTER_ARRAY_FIELDS = new Set<keyof CaseFilters>([
  "statusIds",
  "statusNames",
  "facilityIds",
  "sendingPracticeId",
  "submittingLabId",
  "surgeryCenterLabId",
  "pathologistIds",
  "currentStatusIds",
  "workflowIds",
  "procedureIds",
  "patientIds",
  "flagDefinitionIds",
]);

const FILTER_STRING_FIELDS = new Set<keyof CaseFilters>([
  "clientId",
  "physicianId",
  "dateFrom",
  "dateTo",
  "dateCollected",
  "search",
  "caseType",
  "currentStep",
]);

const FILTER_BOOLEAN_FIELDS = new Set<keyof CaseFilters>([
  "isBlocked",
  "isRush",
  "isHighPriority",
  "isAdditionalStain",
  "excludeEmptyDrafts",
  "onlyEmptyDrafts",
  "tatOverdue",
  "hasPendingTests",
  "eligibleForSendOut",
]);

const FILTER_NUMBER_FIELDS = new Set<keyof CaseFilters>(["tatMinDays"]);

const REVIEW_SORT_COLUMNS = new Set(
  ALL_QUEUE_COLUMNS.filter((column) => column.sortable).map(
    (column) => column.id
  )
);

const INCOMING_SORT_COLUMNS = new Set(
  ALL_INCOMING_COLUMNS.filter((column) => column.sortable).map(
    (column) => column.id
  )
);

const CORRECTIONS_SORT_COLUMNS = new Set([
  "caseNumber",
  "patient",
  "requestor",
  "fieldCount",
]);

const SUMMARY_FILTERS = new Set([
  "new",
  "stat",
  "overdue",
  "pendingStains",
  "discussion",
  "malignancy",
  "qaReview",
]);

const DATE_ONLY_FIELDS = new Set<keyof CaseFilters>([
  "dateFrom",
  "dateTo",
  "dateCollected",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function isDateOnly(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function decodeFilters(
  raw: unknown,
  tab: PathologistQueueViewTab
): CaseFilters | null {
  if (!isRecord(raw)) return null;

  const filters: Record<string, unknown> = {};
  let valid = true;
  Object.entries(raw).forEach(([key, value]) => {
    if (value === undefined) return;
    const field = key as keyof CaseFilters;
    if (tab === "corrections" && field !== "prefixKeys" && field !== "search") {
      valid = false;
      return;
    }
    if (field === "prefixKeys") {
      if (
        !Array.isArray(value) ||
        value.length > 32 ||
        !value.every(
          (item) =>
            typeof item === "string" &&
            (item === "__OTHER__" || /^[A-Z]{1,16}$/.test(item))
        )
      )
        valid = false;
      else filters[key] = value;
      return;
    }
    if (FILTER_ARRAY_FIELDS.has(field)) {
      if (
        !Array.isArray(value) ||
        !value.every((item) => typeof item === "string")
      ) {
        valid = false;
      } else {
        filters[key] = value;
      }
    } else if (FILTER_STRING_FIELDS.has(field)) {
      if (typeof value !== "string") valid = false;
      else if (DATE_ONLY_FIELDS.has(field) && !isDateOnly(value)) valid = false;
      else filters[key] = value;
    } else if (FILTER_BOOLEAN_FIELDS.has(field)) {
      if (typeof value !== "boolean") valid = false;
      else filters[key] = value;
    } else if (FILTER_NUMBER_FIELDS.has(field)) {
      if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
        valid = false;
      } else filters[key] = value;
    }
  });
  return valid ? (filters as CaseFilters) : null;
}

function decodeSort(
  tab: PathologistQueueViewTab,
  raw: unknown
): QueueSortState | null | undefined {
  if (raw === null) return null;
  if (!isRecord(raw)) return undefined;
  if (
    typeof raw.column !== "string" ||
    (raw.direction !== "asc" && raw.direction !== "desc")
  ) {
    return undefined;
  }
  const allowed =
    tab === "incoming"
      ? INCOMING_SORT_COLUMNS
      : tab === "corrections"
      ? CORRECTIONS_SORT_COLUMNS
      : REVIEW_SORT_COLUMNS;
  return allowed.has(raw.column)
    ? { column: raw.column, direction: raw.direction }
    : undefined;
}

function decodeSorts(
  tab: PathologistQueueViewTab,
  raw: unknown
): QueueSortState[] | undefined {
  const allowed =
    tab === "incoming"
      ? INCOMING_SORT_COLUMNS
      : tab === "corrections"
      ? CORRECTIONS_SORT_COLUMNS
      : REVIEW_SORT_COLUMNS;
  if (!Array.isArray(raw) || raw.length > allowed.size) {
    return undefined;
  }
  const sorts = raw.map((item) => decodeSort(tab, item));
  if (sorts.some((sort) => !sort)) return undefined;
  const validSorts = sorts as QueueSortState[];
  if (new Set(validSorts.map((sort) => sort.column)).size !== sorts.length) {
    return undefined;
  }
  return validSorts;
}

function defaultTabView(tab: PathologistQueueViewTab): PathologistQueueTabView {
  if (tab === "corrections")
    return { filters: {}, sort: null, sorts: [], page: 1 };
  if (tab === "incoming") return { filters: {}, sort: null, sorts: [] };
  if (tab === "completed")
    return { filters: {}, sort: null, sorts: [], page: 1 };
  if (tab === "review") {
    return {
      filters: {},
      sort: null,
      sorts: [{ column: "caseNumber", direction: "asc" }],
      page: 1,
      blockedPage: 1,
      summaryFilter: null,
    };
  }
  return {
    filters: {},
    sort: { column: "caseNumber", direction: "asc" },
    sorts: [{ column: "caseNumber", direction: "asc" }],
    page: 1,
    blockedPage: 1,
  };
}

function decodeTab(
  tab: PathologistQueueViewTab,
  raw: unknown,
  version: 1 | 2
): PathologistQueueTabView | null {
  if (!isRecord(raw)) return null;
  if (tab === "review" && version === 2 && raw.sort !== null) return null;
  const filters = decodeFilters(raw.filters, tab);
  const legacySort =
    version === 1 || (tab !== "review" && raw.sorts === undefined)
      ? decodeSort(tab, raw.sort)
      : null;
  const sorts =
    version === 1 || raw.sorts === undefined
      ? legacySort === undefined
        ? undefined
        : legacySort
        ? [legacySort]
        : []
      : decodeSorts(tab, raw.sorts);
  if (!filters || legacySort === undefined || !sorts) {
    return null;
  }

  const defaults = defaultTabView(tab);
  if (raw.page !== undefined && !isPositiveInteger(raw.page)) return null;
  if (raw.blockedPage !== undefined && !isPositiveInteger(raw.blockedPage)) {
    return null;
  }
  if (
    raw.summaryFilter !== undefined &&
    raw.summaryFilter !== null &&
    (typeof raw.summaryFilter !== "string" ||
      !SUMMARY_FILTERS.has(raw.summaryFilter))
  ) {
    return null;
  }

  return {
    ...defaults,
    filters,
    sort: tab === "review" || raw.sorts !== undefined ? null : legacySort,
    sorts,
    ...(raw.page !== undefined ? { page: raw.page } : {}),
    ...(raw.blockedPage !== undefined ? { blockedPage: raw.blockedPage } : {}),
    ...(tab === "review" ? { summaryFilter: raw.summaryFilter ?? null } : {}),
  };
}

export function decodePathologistQueueView(
  raw: unknown
): DecodedPathologistQueueView {
  const value: PathologistQueueViewPreference = {
    version: PATHOLOGIST_QUEUE_VIEW_VERSION,
    tabs: {},
  };
  const invalidTabs = new Set<PathologistQueueViewTab>();
  if (raw == null) return { value, invalidTabs };
  if (
    !isRecord(raw) ||
    (raw.version !== 1 && raw.version !== PATHOLOGIST_QUEUE_VIEW_VERSION) ||
    !isRecord(raw.tabs)
  ) {
    PATHOLOGIST_QUEUE_VIEW_TABS.forEach((tab) => invalidTabs.add(tab));
    return { value, invalidTabs };
  }
  const rawTabs = raw.tabs;

  PATHOLOGIST_QUEUE_VIEW_TABS.forEach((tab) => {
    if (rawTabs[tab] !== undefined) {
      const decoded = decodeTab(tab, rawTabs[tab], raw.version as 1 | 2);
      if (!decoded) invalidTabs.add(tab);
      else value.tabs[tab] = decoded;
    }
  });
  return { value, invalidTabs };
}

export function getPathologistQueueTabView(
  preference: PathologistQueueViewPreference,
  tab: PathologistQueueViewTab
): PathologistQueueTabView {
  return preference.tabs[tab] ?? defaultTabView(tab);
}

export function replacePathologistQueueTabView(
  preference: PathologistQueueViewPreference,
  tab: PathologistQueueViewTab,
  next: PathologistQueueTabView | null
): PathologistQueueViewPreference {
  const tabs = { ...preference.tabs };
  if (next) tabs[tab] = next;
  else delete tabs[tab];
  return { version: PATHOLOGIST_QUEUE_VIEW_VERSION, tabs };
}
